import { Maybe } from "@/domains/common/types";
import { SpaceAccountNoteAttachmentModelData } from "@/store/note-attachments/types";
import { OptimisticSyncUpdate, SyncUpdateValue } from "@/store/sync/types";

export type SpaceAccountNoteAttachmentIndexData = {
  associated_note_id: string | null;
  // created_at, pages_processed, and mime_type back the
  // [mime_type+created_at+pages_processed+model_id] compound index used by the
  // PDF-page paywall counter. IndexedDB compound indexes silently drop any row
  // whose key has a null/undefined component, so these are coerced to non-null
  // fallback keys ("" / 0 / "") that still let `between(["application/pdf", …])`
  // exclude non-PDF rows.
  created_at: string;
  pages_processed: number;
  mime_type: string;
};

export class SpaceAccountNoteAttachmentIndexes {
  private data: SyncUpdateValue<SpaceAccountNoteAttachmentModelData> | undefined;

  constructor({
    remoteData,
    optimisticUpdates,
  }: {
    remoteData: Maybe<SyncUpdateValue<SpaceAccountNoteAttachmentModelData>>;
    optimisticUpdates: OptimisticSyncUpdate<SpaceAccountNoteAttachmentModelData>[];
  }) {
    this.data =
      (optimisticUpdates.at(-1)?.value as SyncUpdateValue<SpaceAccountNoteAttachmentModelData>) ||
      remoteData;
  }

  get associated_note_id(): string | null {
    return this.data?.model_data.associated_note_id ?? null;
  }

  get created_at(): string {
    return this.data?.model_data.created_at ?? "";
  }

  get pages_processed(): number {
    return this.data?.model_data.processing_info?.pages_processed ?? 0;
  }

  get mime_type(): string {
    return this.data?.model_data.mime_type ?? "";
  }

  get indexes(): SpaceAccountNoteAttachmentIndexData {
    return {
      associated_note_id: this.associated_note_id,
      created_at: this.created_at,
      pages_processed: this.pages_processed,
      mime_type: this.mime_type,
    };
  }
}
